// Globals
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { categoriesSelector } from '../../feature/selectors';
import { deleteFilterValue, getFilterData, getFilterValues, importFilters } from '../../feature/actionCreators';
import { GetFilterValuesDTO } from '../../dtos/';

// Components
import { Modal, ModalSize, showSuccessToast, useForm } from '@/components';
import { Button, Drawer } from '@/ui';
import { StyledLink, StyledTitleAndLinkWrapper } from '../../components/Styled';
import {
  StyledContentBlock,
  StyledCreateButton,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledTabsWrap,
  StyledButtonsWrapper,
  StyledTitleModal,
  StyledPageWrapper,
  StyledUploadContent,
  StyledDragger,
  StyledContinueBlock,
  StyledButtonsWrap,
  StyledButton,
  StyledImportBlock,
  StyledImportTitle,
  StyledBadge,
  StyledStatus,
} from './FilterValuesListContainerStyles';
import FilterValuesList from '../../components/FilterValuesList/FilterValuesList';
import CreateFilterValueContainer from '../../containers/CreateFilterValueContainer/CreateFilterValueContainer';
import { DocumentIcon, ReplaceIcon } from '@/icons';

// Models
import { FilterImportStatus, IFilterValue } from '@/models/filters.model';
import { ROUTES } from '@/models/router.model';
import { DEFAULT_LOCALE } from '@/models/localization.model';

// Helpers
import { useBoolean, useModal, usePagination } from '@/hooks';
import { CategoriesTabs } from '../../helpers/configs';
import { getFilterImportStatusLabel } from '../../helpers';
import { Resource, usePermissions } from '@/modules/Permissions';

const FilterValuesListContainer = () => {
  const { filterId } = useParams();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const { value: createFormOpened, setFalse: closeCreateForm, setTrue: openCreateForm } = useBoolean();
  const { filterValues, isLoadingFilters, filtersValuesQuery, filterDetails } = useAppState(categoriesSelector);
  const [currentFilterValue, setCurrentFilterValue] = useState<IFilterValue | null>(null);
  const [form] = useForm();
  const importStatus = filterDetails?.latest_filter_value_import?.status;
  const isFilterImportDisabled = importStatus === FilterImportStatus.PROCESSING;

  const onDrawerClose = () => {
    form.resetFields();
    setCurrentFilterValue(null);
    closeCreateForm();
  };

  const { isOpened, openModal, closeModal } = useModal();
  const { t } = useTranslation(['categories']);
  const { currentPage, onPageChange } = usePagination();
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManageFilters = canManage(Resource.FILTER);

  const getFilterValuesListHandler = () => {
    if (!filterId) return;
    const dto = new GetFilterValuesDTO({ page: currentPage, filter_id: filterId, ...filtersValuesQuery });
    dispatch(getFilterValues(dto));
  };

  const handleImport = useCallback(async () => {
    if (!filterId || !selectedFile) return;

    try {
      await dispatch(
        importFilters({
          filter_id: filterId,
          file: selectedFile,
        }),
      ).unwrap();
      showSuccessToast({ message: t('import_saved_success') });
      setSelectedFile(undefined);
    } catch {}
  }, [selectedFile, filterId]);

  const handleDelete = async () => {
    if (!filterId) return;

    try {
      if (currentFilterValue?.id) await dispatch(deleteFilterValue({ filter_id: filterId, id: currentFilterValue.id }));
      getFilterValuesListHandler();
      closeModal();
      setCurrentFilterValue(null);
    } catch {}
  };

  const onValuesChange = async (file: File | Blob) => {
    if (file) setSelectedFile(file as File);
  };

  const onFileReset = async () => {
    setSelectedFile(undefined);
  };

  const onFilterValueDelete = (filterValue: IFilterValue) => {
    setCurrentFilterValue(filterValue);
    openModal();
  };

  const onFilterValueEdit = (filterValue: IFilterValue) => {
    setCurrentFilterValue(filterValue);
    openCreateForm();
  };

  const handleCloseModal = () => {
    closeModal();
    setCurrentFilterValue(null);
  };

  useEffect(() => {
    getFilterValuesListHandler();
  }, [currentPage]);

  useEffect(() => {
    if (filterId) {
      dispatch(getFilterData(filterId));
    }
  }, [filterId]);

  return (
    <StyledPageWrapper>
      <StyledTitleAndLinkWrapper>
        <StyledLink to={`${ROUTES.CATEGORIES}?tab=${CategoriesTabs.FILTERS}`}>
          {t('filter_values_back_to_filters')}
        </StyledLink>
      </StyledTitleAndLinkWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('filter_values_title', { filter: filterDetails?.locale.uk.title })}</StyledPageTitle>
        <StyledTabsWrap>
          {canManageFilters && (
            <StyledCreateButton onClick={openCreateForm}>{t('filter_values_add_label')}</StyledCreateButton>
          )}
        </StyledTabsWrap>
      </StyledHeaderBlock>
      <StyledContentBlock>
        <FilterValuesList
          isLoading={isLoadingFilters}
          currentPage={currentPage}
          canManage={canManageFilters}
          onPageChange={onPageChange}
          onFilterValueDelete={onFilterValueDelete}
          onFilterValueEdit={onFilterValueEdit}
          total={filterValues?.meta?.total ?? 0}
          filterValuesList={filterValues?.data ?? []}
        />
      </StyledContentBlock>
      <StyledImportBlock>
        <StyledImportTitle>{t('filter_imports')}</StyledImportTitle>
        {importStatus && (
          <StyledStatus>
            <StyledLink to={ROUTES.IMPORTS_LIST}>{t('imports_status_label')}</StyledLink>
            <StyledBadge $status={importStatus}>
              <div className="indicator" />
              <span>{getFilterImportStatusLabel(t, importStatus)}</span>
            </StyledBadge>
          </StyledStatus>
        )}

        {canManageFilters && (
          <>
            {selectedFile && (
              <StyledUploadContent>
                <DocumentIcon />
                {selectedFile.name}
              </StyledUploadContent>
            )}
            {!selectedFile && <StyledDragger onChange={onValuesChange} value={selectedFile} acceptance={'.csv'} />}
            {selectedFile && (
              <StyledContinueBlock>
                <StyledButtonsWrap>
                  <StyledButton disabled={isFilterImportDisabled} onClick={handleImport} type={'primary'}>
                    {t('filter_import_start_import')}
                  </StyledButton>
                  <StyledButton onClick={onFileReset}>
                    <ReplaceIcon />
                    {t('filter_import_reset_file')}
                  </StyledButton>
                </StyledButtonsWrap>
              </StyledContinueBlock>
            )}
          </>
        )}
      </StyledImportBlock>
      <Drawer open={createFormOpened} onClose={onDrawerClose}>
        <CreateFilterValueContainer
          form={form}
          onFormSubmit={onDrawerClose}
          filter_id={filterId ?? ''}
          currentFilterValue={currentFilterValue}
        />
      </Drawer>
      <Modal visible={isOpened} onCancel={handleCloseModal} size={ModalSize.LARGE}>
        <StyledTitleModal>
          {t('filter_values_delete_value', {
            title: currentFilterValue?.locale?.[DEFAULT_LOCALE]?.title,
          })}
        </StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={handleCloseModal} type="primary">
            {t('filter_values_back')}
          </Button>
          <Button onClick={handleDelete} type="text">
            {t('filter_values_delete_button')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
    </StyledPageWrapper>
  );
};

export default FilterValuesListContainer;
